'use client';

import cx from 'classnames';
import { ChangeEventHandler, Fragment, MouseEventHandler, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import Mark from './Mark';
import Step from './Step';
import styles from './slider.module.scss';
import { calcPoint } from './util';

interface Props {
  name?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  marks?: Array<{ label: string; value: number }>;
  onChange?: (value: number) => unknown;
  renderTooltip?: (value: number) => string;
  hierarchy?: 'primary' | 'secondary';
  marksStyle?: string;
  disabled?: boolean;
  onFocus?: () => void;
  /**
   * Fraction of (max - min) used as mark snap distance.
   * Default 0.02 (~±40 at 2000x). Leverage slider passes a smaller ratio so the ceiling is selectable precisely.
   */
  snapThresholdRatio?: number;
}

export const Slider = ({
  name,
  min = 0,
  max = 100,
  value = 0,
  step = 1,
  marks,
  onChange,
  onFocus,
  renderTooltip,
  hierarchy = 'primary',
  marksStyle,
  disabled,
  snapThresholdRatio = 0.02,
}: Props) => {
  const [isShowTooltip, setIsShowTooltip] = useState(false);

  const snapThreshold = (max - min) * snapThresholdRatio;

  const snapToMark = (val: number): number => {
    if (!marks) return val;
    for (const mark of marks) {
      if (Math.abs(val - mark.value) <= snapThreshold) return mark.value;
    }
    return val;
  };

  const onCustomChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange?.(snapToMark(Number(e.currentTarget.value)));
  };

  const onCustomClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    onChange?.(Number(e.currentTarget.value));
  };

  const onMouseDown = () => {
    if (!renderTooltip) return;

    const onMouseUp = () => {
      document.removeEventListener('mouseup', onMouseUp);
      setIsShowTooltip(false);
    };

    setIsShowTooltip(true);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div className={cx(styles.wrapper)}>
      <div className={cx(styles.slider, styles[hierarchy], { [styles.disabled]: disabled })}>
        <input
          type='range'
          name={name}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onCustomChange}
          onMouseDown={onMouseDown}
          tabIndex={-1}
          disabled={disabled}
          onFocus={onFocus}
        />
        <div className={styles.track}>
          {isShowTooltip && (
            <div className={styles.tooltip} style={{ left: calcPoint({ min, max, point: value }) }}>
              {renderTooltip?.(value)}
            </div>
          )}
          <div className={styles.filler} style={{ width: calcPoint({ min, max, point: value }) }} />
          {marks?.map((mark) => {
            return (
              <Fragment key={`step-mark-${mark.label}`}>
                <Step min={min} max={max} point={mark.value} isActive={mark.value <= value} />
                <Mark
                  min={min}
                  max={max}
                  point={mark.value}
                  label={mark.label}
                  onClick={onCustomClick}
                  className={marksStyle}
                  disabled={disabled}
                />
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface ControlProps extends Omit<Props, 'value'> {
  name: string;
}

export const ControlSlider = ({ name, onChange: onChangeCallback, ...restProps }: ControlProps) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange },
  } = useController({ name, control });

  return (
    <Slider
      name={name}
      value={value}
      onChange={(newValue) => {
        onChange(newValue);
        onChangeCallback?.(newValue);
      }}
      {...restProps}
    />
  );
};
